import { useEffect, useRef } from 'react'

/**
 * Saves what is typed, a moment after the typing stops.
 *
 * A moment rather than every keystroke: what is being written is a row in a database and a save is a
 * request, so one per character would be a request per character. Half a second is under what anybody
 * notices and well over the gap between two keystrokes.
 *
 * It also saves **on the way out** — when what is open is closed, or the page is left — because the
 * last thing typed is the thing most likely to be lost, and it is the one a timer has not reached yet.
 *
 * In `ui/` rather than beside a note, because three areas write: the notepad, a document in the
 * library, and a sticky note on a board. All three want the same half second and the same save on
 * the way out, and a second copy of that is a second place for the number to drift.
 */
export function useSaving(
  save: (text: string) => Promise<void>,
  after = SAVE_AFTER_MS,
): {
  readonly typed: (text: string) => void
  readonly now: () => void
} {
  const pending = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(save)
  // After the render, not during it — see `Editor`. What is written down is whatever the latest render
  // knows how to write, and the timer below reads it when it fires.
  useEffect(() => {
    latest.current = save
  })

  const write = useRef(() => {
    const text = pending.current
    if (text === null) {
      return
    }
    pending.current = null
    void latest.current(text)
  })

  const typed = (text: string) => {
    pending.current = text
    if (timer.current !== null) {
      clearTimeout(timer.current)
    }
    timer.current = setTimeout(() => {
      write.current()
    }, after)
  }

  const now = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    write.current()
  }

  useEffect(
    () => () => {
      // On the way out: the component is going, and whatever was typed in the last half second would
      // otherwise go with it.
      if (timer.current !== null) {
        clearTimeout(timer.current)
      }
      write.current()
    },
    [],
  )

  return { typed, now }
}

/** How long after the typing stops a text is written down. */
export const SAVE_AFTER_MS = 600
